import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn, formatCurrency } from '@/lib/utils';
import { Share2, Check } from 'lucide-react';
import { UNIT_VALUE } from '@/lib/score';

interface PlayerResult {
  name: string;
  buyIns: number;
  cashOut: number;
  net: number;
}

interface SessionSummaryModalProps {
  open: boolean;
  results: PlayerResult[];
  onConfirm: () => void;
  saving: boolean;
  buyInUnit?: number;
  sessionId?: string | null;
}

export default function SessionSummaryModal({ open, results, onConfirm, saving, buyInUnit, sessionId }: SessionSummaryModalProps) {
  const sorted = [...results].sort((a, b) => b.net - a.net);
  const totalPot = results.reduce((s, r) => s + (r.buyIns * (buyInUnit ?? UNIT_VALUE)), 0);
  const totalWon = results.reduce((s, r) => r.net > 0 ? s + r.net : s, 0);
  const totalLost = results.reduce((s, r) => r.net < 0 ? s + r.net : s, 0);

  const buildShareText = () => {
    let text = `🏆 SSC Session Results\n`;
    text += `💰 Total Pot: ${formatCurrency(totalPot)}\n`;
    text += `📈 Total Won: ${formatCurrency(totalWon)}\n`;
    text += `📉 Total Lost: ${formatCurrency(totalLost)}\n\n`;
    sorted.forEach((r, i) => {
      const emoji = r.net > 0 ? '🟢' : r.net < 0 ? '🔴' : '⚪';
      text += `${emoji} ${r.name}: ${r.net >= 0 ? '+' : ''}${formatCurrency(r.net)} (${r.buyIns} entr${r.buyIns > 1 ? 'ies' : 'y'} → ${formatCurrency(r.cashOut)})\n`;
    });
    
    if (sessionId) {
      const url = `${window.location.origin}/session/${sessionId}`;
      text += `\nFull results & portal: ${url}`;
    }
    
    return text;
  };

  const shareWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(buildShareText())}`, '_blank');
  };

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent className="max-w-sm mx-auto" onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>Session Summary</DialogTitle>
        </DialogHeader>

        <div className="text-center mb-2">
          <p className="text-sm text-muted-foreground uppercase font-bold tracking-wider mb-1">Session Summary</p>
          <div className="grid grid-cols-2 gap-4 my-4">
            <div>
              <p className="text-[10px] text-muted-foreground uppercase font-bold">Total Won</p>
              <p className="text-xl font-bold font-mono text-success">{formatCurrency(totalWon)}</p>
            </div>
            <div>
              <p className="text-[10px] text-muted-foreground uppercase font-bold">Total Lost</p>
              <p className="text-xl font-bold font-mono text-destructive">{formatCurrency(totalLost)}</p>
            </div>
          </div>
          <p className="text-[10px] text-muted-foreground uppercase font-bold mt-1">
            Total Pot: {formatCurrency(totalPot)}
          </p>
        </div>

        <ScrollArea className="max-h-[50vh] pr-4">
          <div className="space-y-1">
            {sorted.map((r, i) => (
              <div key={i} className="flex justify-between items-center py-1.5 border-b border-border/50 last:border-0">
                <div>
                  <p className="font-medium text-sm">{r.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {r.buyIns} entr{r.buyIns > 1 ? 'ies' : 'y'} → {formatCurrency(r.cashOut)}
                  </p>
                </div>
                <span className={cn(
                  'font-mono font-bold',
                  r.net > 0 ? 'text-success' : r.net < 0 ? 'text-destructive' : 'text-muted-foreground'
                )}>
                  {r.net >= 0 ? '+' : ''}{formatCurrency(r.net)}
                </span>
              </div>
            ))}
          </div>
        </ScrollArea>

        <div className="flex gap-2 mt-2">
          <Button variant="outline" className="flex-1 gap-1" onClick={shareWhatsApp}>
            <Share2 className="h-4 w-4" /> WhatsApp
          </Button>
          <Button className="flex-1 gap-1" onClick={onConfirm} disabled={saving}>
            <Check className="h-4 w-4" /> {saving ? 'Saving...' : 'End Session'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
